// How the lobby camera's look is driven: a mouse movement in pixels, in and out. Kept
// out of three.js's way (like lobbyWalk.js) so it can be tested under jsdom, which
// cannot import the 3D chain.
//
// The look is a delta, not a position. It used to aim at wherever the cursor sat, which
// meant a pointer left resting past a certain point kept turning the camera with nothing
// being gestured, and the only way to stop it was to move the pointer the other way —
// which span it the other way. A movement of nothing now does nothing, so a still mouse
// can never turn the camera on its own. That is also what lets yaw be uncapped: the same
// movement always adds the same turn, however far round the camera has already come.

/**
 * Radians of turn per pixel of mouse movement. A full 360 comes round in about 2,500
 * pixels of travel, which is a comfortable sweep of a desktop mouse or a trackpad.
 */
export const LOOK_SENSITIVITY = 0.0025;

/**
 * How far the camera will look up or down. Pitch is the one axis still clamped: yaw
 * turns freely and all the way round, but an unclamped pitch would carry the camera over
 * onto its back once it passed vertical.
 */
export const LOOK_PITCH_LIMIT = Math.PI / 9;

/**
 * The look `{ yaw, pitch }` after a movement of `dx`/`dy` pixels. Movements are in the
 * browser's own sense: X to the right, Y downwards.
 *
 * Both axes come out negated. The camera looks down -Z, so a rotation about Y turns its
 * nose towards -X — positive yaw is a look to the left — and client Y grows downwards
 * while screen pitch does not. Yaw is left unclamped; pitch is held to LOOK_PITCH_LIMIT.
 *
 * Pure: the look it is given is not touched, and a movement it cannot make sense of
 * comes back as the look unchanged rather than as NaN angles.
 */
export function applyLookDelta(look, dx, dy) {
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return { yaw: look.yaw, pitch: look.pitch };

    return {
        yaw: look.yaw - dx * LOOK_SENSITIVITY,
        pitch: clampPitch(look.pitch - dy * LOOK_SENSITIVITY),
    };
}

function clampPitch(pitch) {
    return Math.max(-LOOK_PITCH_LIMIT, Math.min(LOOK_PITCH_LIMIT, pitch));
}
